if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors =
          this.dataset.hideErrors ===
          'true';

        this.isColdSugarFullProductPage =
          Boolean(
            this.closest(
              'product-info'
            )?.querySelector(
              '[data-cs-product-page-docks]'
            )
          ) &&
          String(
            this.submitButton?.id ||
            ''
          ).startsWith(
            'ProductSubmitButton-'
          );
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.setLoadingState(true);

        const orderMode =
          this.getOrderMode();

        let shoppingContext =
          this.getShoppingContext(
            orderMode
          );

        let acceptedQuantity =
          this.getRequestedQuantity();

        try {
          if (
            orderMode === 'local' &&
            !shoppingContext
          ) {
            shoppingContext =
              await this
                .getCartShoppingContext();
          }

          if (
            orderMode === 'local' &&
            !shoppingContext
          ) {
            throw new Error(
              'Choose your pickup location from Shop before adding this item.'
            );
          }

          if (!window.ColdSugarOrderMode) {
            throw new Error(
              'Order protection is still loading. Please wait a moment and try again.'
            );
          }

          const allowed = await window.ColdSugarOrderMode.beforeAdd({
            orderMode,
            trigger: this.submitButton,
          });

          if (!allowed) return;

          if (
            orderMode ===
            'shipping'
          ) {
            acceptedQuantity =
              await this
                .validateShippingBoxCapacity(
                  acceptedQuantity
                );
          }

          if (shoppingContext) {
            await this.validateShoppingContext(shoppingContext);
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData =
            new FormData(
              this.form
            );

          acceptedQuantity =
            Math.max(
              1,
              Number.parseInt(
                acceptedQuantity,
                10
              ) ||
              1
            );

          formData.set(
            'quantity',
            String(
              acceptedQuantity
            )
          );

          formData.set(
            'properties[_cold_sugar_order_mode]',
            orderMode
          );
          if (shoppingContext) {
            formData.set('properties[_cold_sugar_shopping_path]', shoppingContext.shoppingPath);
          }

          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }

          config.body = formData;

          const fetchResponse = await fetch(`${routes.cart_add_url}`, config);
          let response = await fetchResponse.json();

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.errors || response.description,
              message: response.message,
            });

            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');

            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }

            this.error = true;
            return;
          }

          if (shoppingContext) {
            response = await this.syncShoppingContext(shoppingContext, response);
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          }

          this.error = false;

          const quickAddModal =
            this.closest(
              'quick-add-modal'
            );

          if (
            this.isColdSugarFullProductPage
          ) {
            if (
              this.cart?.tagName ===
                'CART-NOTIFICATION' &&
              typeof
                this.cart
                  .renderContents ===
                'function'
            ) {
              await this.cart
                .renderContents(
                  response,
                  {
                    open: false
                  }
                );
            } else if (
              this.cart &&
              typeof
                this.cart
                  .renderContents ===
                'function'
            ) {
              this.cart.renderContents(
                response
              );

              window.requestAnimationFrame(
                () => {
                  this.cart
                    ?.close?.();
                }
              );
            }

            const refreshedCart =
              await this
                .fetchCartJSON();

            document.dispatchEvent(
              new CustomEvent(
                'cart:updated',
                {
                  bubbles: true,
                  detail: {
                    cart:
                      refreshedCart,
                    expandColdSugarDock:
                      true
                  }
                }
              )
            );

            await window
              .ColdSugarProductPageCart
              ?.refresh?.({
                cart:
                  refreshedCart,
                expand:
                  true
              });
          } else if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  this.cart.renderContents(
                    response
                  );
                });
              },
              {
                once: true
              }
            );

            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(
              response
            );
          }
        } catch (error) {
          console.error(error);
          this.error = false;
          this.handleErrorMessage(
            error.message || 'Something went wrong. Please try again.'
          );
        } finally {
          this.setLoadingState(false);
        }
      }

      getShoppingContext(orderMode) {
        if (orderMode !== 'local') return null;

        const params = new URLSearchParams(window.location.search);
        let shoppingPath = String(params.get('shopping_path') || '').trim().toLowerCase();
        let fulfillmentHandle = String(params.get('fulfillment') || '').trim();

        if (!shoppingPath || !fulfillmentHandle) {
          try {
            const stored = JSON.parse(sessionStorage.getItem('coldSugarShoppingContext') || '{}');
            shoppingPath = shoppingPath || String(stored.shoppingPath || '').trim().toLowerCase();
            fulfillmentHandle = fulfillmentHandle || String(stored.fulfillmentHandle || '').trim();
          } catch (error) {
            // Continue without a preselected context.
          }
        }

        const supportedPaths = [
          'market_pickup',
          'bakehouse_order_ahead',
        ];

        if (!supportedPaths.includes(shoppingPath) || !fulfillmentHandle) return null;

        const data = document.getElementById('ColdSugarGlobalFulfillmentOptionsJSON');
        if (!data) return null;

        try {
          const options = JSON.parse(data.textContent || '[]');
          const option = options.find((item) => item.handle === fulfillmentHandle);

          if (
            !option ||
            String(option.fulfillment_category || '').toLowerCase() !== 'pickup'
          ) {
            return null;
          }

          const subtype = String(option.pickup_subtype || '').toLowerCase();

          if (subtype === 'porch_pickup') return null;

          if (
            shoppingPath === 'bakehouse_order_ahead' &&
            subtype !== 'bakehouse_order_ahead'
          ) {
            return null;
          }

          if (
            shoppingPath === 'market_pickup' &&
            subtype === 'bakehouse_order_ahead'
          ) {
            return null;
          }

          return { shoppingPath, fulfillmentHandle, option };
        } catch (error) {
          console.error('Cold Sugar product fulfillment context could not be read.', error);
          return null;
        }
      }

      async getCartShoppingContext() {
        const cart =
          await this.fetchCartJSON();

        const attributes =
          cart?.attributes ||
          {};

        const shoppingPath =
          String(
            attributes[
              'Shopping Path'
            ] ||
            ''
          )
            .trim()
            .toLowerCase();

        const fulfillmentHandle =
          String(
            attributes[
              'Fulfillment Option Handle'
            ] ||
            ''
          )
            .trim();

        if (
          ![
            'market_pickup',
            'bakehouse_order_ahead'
          ].includes(
            shoppingPath
          ) ||
          !fulfillmentHandle
        ) {
          return null;
        }

        const data =
          document.getElementById(
            'ColdSugarGlobalFulfillmentOptionsJSON'
          );

        if (!data) {
          return null;
        }

        try {
          const options =
            JSON.parse(
              data.textContent ||
              '[]'
            );

          const option =
            options.find(
              (item) =>
                String(
                  item.handle ||
                  ''
                ) ===
                fulfillmentHandle
            );

          if (!option) {
            return null;
          }

          if (
            String(
              option.fulfillment_category ||
              ''
            )
              .trim()
              .toLowerCase() !==
            'pickup'
          ) {
            return null;
          }

          const subtype =
            String(
              option.pickup_subtype ||
              ''
            )
              .trim()
              .toLowerCase();

          if (
            subtype ===
            'porch_pickup'
          ) {
            return null;
          }

          if (
            shoppingPath ===
              'bakehouse_order_ahead' &&
            subtype !==
              'bakehouse_order_ahead'
          ) {
            return null;
          }

          if (
            shoppingPath ===
              'market_pickup' &&
            subtype ===
              'bakehouse_order_ahead'
          ) {
            return null;
          }

          return {
            shoppingPath,
            fulfillmentHandle,
            option
          };
        } catch (error) {
          console.error(
            'Cold Sugar cart fulfillment context could not be read.',
            error
          );

          return null;
        }
      }

      isLockItem(item) {
        const value = item?.properties?._cold_sugar_pickup_lock;
        return value === true || value === 'true' || item?.handle === 'pickup-location';
      }

      async fetchCartJSON() {
        const response = await fetch((window.routes?.cart_url || '/cart') + '.js', {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error('Your cart could not be checked. Please try again.');
        return response.json();
      }

      async validateShoppingContext(context) {
        const cart = await this.fetchCartJSON();
        const visibleItems = (cart.items || []).filter((item) => !this.isLockItem(item));
        const existingPath = String(cart.attributes?.['Shopping Path'] || '').trim().toLowerCase();
        const existingHandle = String(cart.attributes?.['Fulfillment Option Handle'] || '').trim();
        const existingLock = (cart.items || []).find((item) => this.isLockItem(item));
        const existingLockHandle = String(existingLock?.properties?._cold_sugar_fulfillment_option || '').trim();

        if (visibleItems.length && (!existingPath || existingPath !== context.shoppingPath)) {
          throw new Error('Your cart is already for another shopping option. Review or clear it before starting this order.');
        }

        if (visibleItems.length && existingHandle && existingHandle !== context.fulfillmentHandle) {
          throw new Error(`Your cart is already set for another pickup location. Review your cart before switching to ${context.option.display_name}.`);
        }

        if (existingLockHandle && existingLockHandle !== context.fulfillmentHandle) {
          throw new Error('Your cart is locked to another pickup location. Review your cart before continuing.');
        }
      }

      async syncShoppingContext(context, fallbackResponse) {
        const option = context.option;
        const isOrderAhead =
          context.shoppingPath === 'bakehouse_order_ahead';

        const optionName = isOrderAhead
          ? 'Cold Sugar Bakehouse — Order Ahead'
          : (
              option.display_name ||
              ''
            );

        const locationName = isOrderAhead
          ? 'Cold Sugar Bakehouse — Parmer Ranch'
          : (
              option.display_name ||
              ''
            );

        const attributes = {
          'Shopping Path':
            context.shoppingPath ||
            'market_pickup',

          'Porch Live Purchase': '',

          'Thursday Preorder Close Time': '',
          'Thursday Live Open Time': '',
          'Thursday Live Close Time': '',

          'Fulfillment Category':
            isOrderAhead
              ? 'order_ahead'
              : 'pickup',

          'Fulfillment Type':
            isOrderAhead
              ? 'Pickup'
              : 'Pickup',

          'Fulfillment Option Handle':
            option.handle || '',

          'Fulfillment Option Name':
            optionName,

          'Pickup Event ID': '',
          'Pickup Date': '',
          'Pickup Day': '',

          'Pickup Location':
            locationName,

          'Pickup City State':
            option.city_state || '',

          'Pickup Address':
            option.full_address || '',

          'Pickup Window':
            isOrderAhead
              ? ''
              : (
                  option.time_window ||
                  option.display_schedule ||
                  ''
                ),

          'Pickup Instructions':
            isOrderAhead
              ? ''
              : (
                  option.customer_note ||
                  ''
                ),

          'Delivery Event ID': '',
          'Delivery Date': '',
          'Delivery Day': '',

          'Delivery Area': '',

          'Delivery Window': '',
          'Delivery Zip Code': '',
          'Delivery Instructions': '',

          'Shipping Event ID': '',
          'Shipping Option': '',

          'Requested Delivery Date': '',
          'Requested Delivery Day': '',
          'Estimated Ship Date': '',
          'Shipping Method': '',

          'Checkout Method To Select':
            isOrderAhead
              ? 'Choose Pickup in store at checkout.'
              : (
                  option.checkout_instruction ||
                  `Choose Pickup in store → ${option.display_name || 'your selected market'}.`
                ),
        };

        await fetch(window.routes?.cart_update_url || '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes }),
        });

        const cart = await this.fetchCartJSON();
        const existingLock = (cart.items || []).find((item) => this.isLockItem(item));
        if (existingLock) return fallbackResponse;

        const lockId = Number(option.pickup_lock_variant_id || 0);
        if (!lockId) throw new Error('This pickup location is not connected to its checkout lock yet.');

        const payload = {
          items: [{
            id: lockId,
            quantity: 1,
            properties: {
              _cold_sugar_pickup_lock: 'true',
              _cold_sugar_fulfillment_option: option.handle || '',
            },
          }],
        };

        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload),
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || 'The pickup location could not be locked. Please try again.');
        }
        return result;
      }

      getShippingPackType() {
        const hiddenPackType =
          this.form.querySelector(
            '[name="properties[_cold_sugar_shipping_pack_type]"]'
          );

        return String(
          this.dataset.shippingPackType ||
          hiddenPackType?.value ||
          ''
        )
          .trim()
          .toLowerCase();
      }

      getQuantityInput() {
        /*
          The main Dawn quantity selector can live outside the
          literal <form>, connected with form="product-form-*".

          form.elements is the browser-native source of truth
          and includes those externally-associated controls.
        */
        const associated =
          this.form?.elements
            ?.namedItem(
              'quantity'
            );

        if (associated) {
          /*
            namedItem can theoretically return a RadioNodeList.
            Quantity is normally one input, but handle both.
          */
          if (
            typeof associated.value !==
              'undefined'
          ) {
            return associated;
          }

          if (
            associated.length
          ) {
            return associated[0];
          }
        }

        /*
          Fallback only for unusual theme/editor rendering.
        */
        const formId =
          String(
            this.form?.id ||
            ''
          ).trim();

        if (formId) {
          const escapedFormId =
            typeof CSS !==
              'undefined' &&
            typeof CSS.escape ===
              'function'
              ? CSS.escape(
                  formId
                )
              : formId.replace(
                  /["\\]/g,
                  '\\$&'
                );

          const linked =
            document.querySelector(
              `[name="quantity"][form="${escapedFormId}"]`
            );

          if (linked) {
            return linked;
          }
        }

        return this.form.querySelector(
          '[name="quantity"]'
        );
      }


      getRequestedQuantity() {
        const quantityInput =
          this.getQuantityInput();

        const quantity =
          Number.parseInt(
            quantityInput?.value || '1',
            10
          );

        return Number.isFinite(quantity) &&
          quantity > 0
          ? quantity
          : 1;
      }

      async validateShippingBoxCapacity(
        requestedQuantity =
          this.getRequestedQuantity()
      ) {
        const shippingBox =
          window.ColdSugarShippingBox;

        if (
          !shippingBox ||
          typeof
            shippingBox.previewAdd !==
            'function'
        ) {
          throw new Error(
            'Shipping box protection is still loading. Please wait a moment and try again.'
          );
        }

        const packType =
          this.getShippingPackType();

        if (!packType) {
          throw new Error(
            'This shipped item is missing its box-packing information. Please contact us before ordering.'
          );
        }

        const quantityInput =
          this.getQuantityInput();

        const minimum =
          Math.max(
            1,
            Number(
              quantityInput?.min ||
              quantityInput
                ?.dataset?.min ||
              1
            ) ||
            1
          );

        const increment =
          Math.max(
            1,
            Number(
              quantityInput?.step ||
              1
            ) ||
            1
          );

        let acceptedQuantity =
          Math.max(
            minimum,
            Number.parseInt(
              requestedQuantity,
              10
            ) ||
            minimum
          );

        const cart =
          await this.fetchCartJSON();

        let result =
          shippingBox.previewAdd(
            cart.items || [],
            packType,
            acceptedQuantity
          );

        /*
          "I want 6" when only 3 more fit means:
          add the greatest valid amount, not 1 and not zero.
        */
        if (!result.valid) {
          let foundQuantity =
            0;

          for (
            let candidate =
              acceptedQuantity -
              increment;
            candidate >= minimum;
            candidate -= increment
          ) {
            const candidateResult =
              shippingBox.previewAdd(
                cart.items || [],
                packType,
                candidate
              );

            if (
              candidateResult.valid
            ) {
              acceptedQuantity =
                candidate;

              result =
                candidateResult;

              foundQuantity =
                candidate;

              break;
            }
          }

          if (
            foundQuantity <= 0
          ) {
            throw new Error(
              result.message ||
              'That combination will not fit in our available shipping boxes.'
            );
          }
        }

        if (quantityInput) {
          quantityInput.value =
            String(
              acceptedQuantity
            );

          /*
            Keep Dawn's quantity component aware that its value
            was intentionally changed before the cart request.
          */
          quantityInput.dispatchEvent(
            new Event(
              'change',
              {
                bubbles: true
              }
            )
          );
        }

        return acceptedQuantity;
      }


      getOrderMode() {
        const hiddenMode = this.form.querySelector(
          '[name="properties[_cold_sugar_order_mode]"]'
        );

        return String(this.dataset.orderMode || hiddenMode?.value || '')
          .trim()
          .toLowerCase();
      }

      setLoadingState(isLoading) {
        const spinner = this.querySelector('.loading__spinner');

        this.submitButton.classList.toggle('loading', isLoading);

        if (isLoading) {
          this.submitButton.setAttribute('aria-disabled', true);
          spinner?.classList.remove('hidden');
          return;
        }

        if (this.cart && this.cart.classList.contains('is-empty')) {
          this.cart.classList.remove('is-empty');
        }

        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
        }

        spinner?.classList.add('hidden');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}